import React from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import { MdRefresh, MdStar } from 'react-icons/md';
import { Button, TagPill } from '@aibuild/ui';
import type { AppRunStatus } from '@api/apps';
import type { AppRunController } from '../../appFrame/useAppRunController';
import RunnerTag from '../../runSetup/RunnerTag';
import { useKeepAsExample } from './useKeepAsExample';
import { BarRoot, StaleNote } from './PreviewRunBar.styled';

const STATUS_MESSAGES: Readonly<Record<AppRunStatus, { id: string; defaultMessage: string }>> = {
  DRAFT: { id: 'apps.builder.run.status.draft', defaultMessage: 'Test run' },
  RUNNING: { id: 'apps.builder.run.status.running', defaultMessage: 'Test run · working' },
  WAITING_ON_USER: { id: 'apps.builder.run.status.waiting', defaultMessage: 'Test run · waiting for you' },
  SUCCEEDED: { id: 'apps.builder.run.status.succeeded', defaultMessage: 'Test run · done' },
  FAILED: { id: 'apps.builder.run.status.failed', defaultMessage: 'Test run · failed' },
  CANCELLED: { id: 'apps.builder.run.status.cancelled', defaultMessage: 'Test run · stopped' },
};

const statusLabel = (intl: IntlShape, controller: AppRunController, status: AppRunStatus): string => {
  if (controller.stopping) return intl.formatMessage({ id: 'apps.builder.run.status.stopping', defaultMessage: 'Test run · stopping' });
  return intl.formatMessage(controller.isBusy ? STATUS_MESSAGES.RUNNING : STATUS_MESSAGES[status]);
};

interface PreviewRunBarProps {
  readonly appKey: string;
  readonly controller: AppRunController;
  readonly agent: boolean;
  readonly stale: boolean;
  readonly lastOptions: Readonly<Record<string, unknown>> | null;
}

const PreviewRunBar: React.FC<PreviewRunBarProps> = ({ appKey, controller, agent, stale, lastOptions }) => {
  const intl = useIntl();
  const { run } = controller;
  const keep = useKeepAsExample(appKey, run, agent, (controller.manifest?.options.length ?? 0) > 0, lastOptions);
  const busy = controller.isBusy || controller.submitting;

  return (
    <BarRoot data-testid="app-builder__run-bar">
      {run && <TagPill label={statusLabel(intl, controller, run.status)} prefix={false} size="sm" dataTestId="app-builder__run-status" />}
      <RunnerTag controller={controller} />
      {stale && (
        <StaleNote role="status" data-testid="app-builder__run-stale">
          {intl.formatMessage({
            id: 'apps.builder.run.stale',
            defaultMessage: 'The app changed since this run — try the latest version',
          })}
        </StaleNote>
      )}
      {keep.visible && (
        <Button
          variant="outlined"
          size="sm"
          leadingIcon={<MdStar size={16} aria-hidden="true" />}
          disabled={!keep.ready || busy}
          loading={keep.saving}
          onClick={keep.save}
          dataTestId="app-builder__keep-example"
        >
          {intl.formatMessage({ id: 'apps.builder.run.keepExample', defaultMessage: 'Keep as example' })}
        </Button>
      )}
      {run && (
        <Button
          variant="text"
          size="sm"
          leadingIcon={<MdRefresh size={16} aria-hidden="true" />}
          disabled={busy}
          onClick={controller.newRun}
          dataTestId="app-builder__start-over"
        >
          {intl.formatMessage({ id: 'apps.builder.run.startOver', defaultMessage: 'Start over' })}
        </Button>
      )}
    </BarRoot>
  );
};

export default PreviewRunBar;
